import React, { useState, useRef } from 'react';
import { ChevronDown, ChevronUp, BookOpenText } from 'lucide-react';
import { motion, AnimatePresence, useInView } from 'framer-motion';

const AccordionItem = ({ title, content, index }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="accordion-item"
    >
      <button
        className="accordion-button"
        onClick={() => setOpen(!open)}
      >
        <span className="accordion-title">
          <BookOpenText className="accordion-icon" /> {title}
        </span>
        {open ? <ChevronUp className="accordion-chevron" /> : <ChevronDown className="accordion-chevron" />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="accordion-content"
          >
            {typeof content === 'string' ? <p>{content}</p> : content}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const WikiAccordion = () => {
  const entries = [
    {
      title: "What is Global Savings Pools?",
      content: "Global Savings Pools is a decentralized application (DApp) built on the BNB Smart Chain Mainnet, enabling users to grow their USDT through structured contribution pools. Each pool operates like a micro-financial circle: one center and several participants. As pools fill, rewards are unlocked, and users progress through increasingly rewarding levels."
    },
    {
      title: "Connecting Your Wallet",
      content: (
        <ol className="list-decimal list-inside space-y-1">
          <li>
  Create or Download (
  <a className="metamask"
    href="https://metamask.io/download"
    target="_blank"
    rel="noopener noreferrer">MetaMask</a> or compatible).</li>
          <li>Connect your WEB3 wallet.</li>
          <li>Ensure you're on the BNB Mainnet.</li>
          <li>Make sure you hold enough USDT (BEP-20).</li>
        </ol>
      )
    },
    {
      title: "Approving USDT",
      content: "Before joining or creating a pool, the DApp will check your USDT allowance and prompt approval for only the amount needed. If already approved, you can proceed directly."
    },
    {
      title: "Joining a Pool",
      content: (
        <ol className="list-decimal list-inside space-y-1">
          <li>Choose a level you're eligible for.</li>
          <li>Select an available pool that isn’t full.</li>
          <li>Click \"Join Pool\" and confirm the USDT transfer.</li>
          <li>Note: You cannot join your own pool or join a pool more than once.</li>
        </ol>
      )
    },
    {
      title: "Creating a Pool",
      content: "Select your unlocked level and click 'Create Pool'. You'll become the center and invest the required USDT. A referral link is generated for inviting friends."
    },
    {
      title: "Level Structure & Rewards",
      content: (
        <table className="w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border px-2 py-1">Level</th>
              <th className="border px-2 py-1">Participants</th>
              <th className="border px-2 py-1">Investment</th>
              <th className="border px-2 py-1">Reward</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Entry", 3, "16 USDT", "43 USDT"],
              ["Stacker", 4, "32 USDT", "115 USDT"],
              ["Scaler", 5, "64 USDT", "288 USDT"],
              ["Flipper", 6, "128 USDT", "691 USDT"],
              ["Holder", 5, "256 USDT", "1152 USDT"],
              ["Broker", 4, "512 USDT", "1843 USDT"],
              ["Tycoon", 3, "1024 USDT", "2475 USDT"],
              ["Legend", 10, "2048 USDT", "18432 USDT"],
            ].map(([lvl, part, invest, reward], i) => (
              <tr key={i}>
                <td className="border px-2 py-1">{lvl}</td>
                <td className="border px-2 py-1">{part}</td>
                <td className="border px-2 py-1">{invest}</td>
                <td className="border px-2 py-1">{reward}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    },
    {
      title: "Claiming Rewards",
      content: "Only the center of a full pool can claim rewards. The reward includes 90% of the total USDT contributed by participants. After claiming you can Join or Create a new pool and your next level unlocks automatically or you can withdraw the full amount, keep the current level and keep creating same level pools."
    },
    {
      title: "Emergency Exit",
      content: "If you're the center and no one has joined your pool, you can activate an Emergency Exit and get refunded 10% of your investment. The pool is marked as exited and cannot be reused."
    },
    {
      title: "Smart Contract Security",
      content: "All contracts are immutable, on-chain verified, and have no admin keys. This ensures transparent and secure operations on the BNB Smart Chain Mainnet."
    },
    {
      title: "Referral System",
      content: (
        <div>
          <p>Each created pool generates a referral link in the format:</p>
          <code className="block bg-gray-100 dark:bg-gray-800 p-2 my-2 rounded">https://globalsavingspools.com/?ref=levelId-BlockId</code>
          <p>Share this with your friends to help fill your pool faster!</p>
        </div>
      )
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-5xl mx-auto px-4 py-10"
    >
      <h1 className="wiki-header">
        📚 Global Savings Pools Wiki
      </h1>
      {entries.map((item, idx) => (
        <AccordionItem key={idx} index={idx} title={item.title} content={item.content} />
      ))}
    </motion.div>
  );
};

export default WikiAccordion;
